import { accountToAgeGenderAnalytics } from '@/libs/analytics/transformers';
import { AccountAccount } from '@calm/evexia-web-sdk';
import { useTracking } from '@rhg/libs/analytics';
import createAccountApi from '@rhg/libs/api/create-account-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import useDatadog from '@rhg/libs/datadog/use-datadog';
import { queryKeys, useQuery, useMutation } from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { getStoredIdpToken, getStoredUserId } from '@rhg/utils/sso';
import { getAnalyticsLoginMethodByUserId } from '@rhg/utils/user-id';
import { useIsAgeGenderEnabled } from '../use-age-gender-enabled';
import { useIsSamlUser } from '../use-is-saml-user';

export const queryKey = [queryKeys.accountSync, queryKeys.refreshAccessToken];

/**
 * Makes a POST request to sync the user's account. Identifies the user
 * with datadog and analytics.
 */
export const useAccount = () => {
    const {
        user,
        isAuthenticated,
        getIdTokenClaims,
        getAccessTokenSilently,
        isLoading: isAuthLoading,
    } = useAuth();
    const { identify, register, setUserProfile } = useTracking();
    const { setUser } = useDatadog();
    const api = createAccountApi(getAccessTokenSilently);
    const enabled = !!isAuthenticated;
    const rHGApp = getRhgAppHeader();
    const isSamlUser = useIsSamlUser();
    const isAgeGenderEnabled = useIsAgeGenderEnabled();

    const {
        data: account,
        isLoading: isSyncLoading,
        ...otherProps
    } = useQuery<AccountAccount, Error>({
        queryKey,
        queryFn: async () => {
            // Skip account syncing but return user ID for circle logic
            if (isSamlUser) {
                const response = {
                    userId: user?.sub,
                } as AccountAccount;
                return response;
            }

            const claims = await getIdTokenClaims();
            const response = await api.syncAccountsV1({
                rHGApp,
                body: claims?.__raw ?? '',
            });

            const { analyticsId, userId } = response.data;

            identify(analyticsId);
            // Same as setUserProperties in amplitude
            register({
                LoginMethod: getAnalyticsLoginMethodByUserId(userId),
            });
            if (isAgeGenderEnabled) {
                setUserProfile(accountToAgeGenderAnalytics(response.data));
            }
            setUser(userId);

            return response.data;
        },
        enabled,
        staleTime: 1000 * 60 * 60 * 8, // 8 hours,
    });

    return {
        account,
        enabled,
        isLoading: isAuthLoading || isSyncLoading,
        ...otherProps,
    };
};

// Here we pass in an optional refetch interval to facilitate testing
export function useIsEmailVerified({
    isEnabled = true,
    refetchInterval = 10000,
}: {
    isEnabled?: boolean;
    refetchInterval?: number;
}) {
    const { getAccessTokenSilently, isLoading: isAuthLoading } = useAuth();
    const {
        account,
        isLoading: isAccountLoading,
        enabled: accountEnabled,
        refetch: refetchAccount,
    } = useAccount();
    const enabled = isEnabled && accountEnabled && !account?.emailVerified;

    const otherProps = useQuery({
        queryKey: [queryKeys.refreshAccessToken],
        queryFn: async () => {
            const token = await getAccessTokenSilently({ cacheMode: 'off' });
            await refetchAccount();
            return token;
        },
        enabled,
        refetchOnWindowFocus: 'always',
        refetchInterval,
    });

    return {
        ...otherProps,
        enabled,
        isEmailVerified: account?.emailVerified,
        isLoading: isAuthLoading || isAccountLoading,
    };
}

interface AccountLinkAccountsParams {
    userId: string;
    primaryUserId: string;
    secondaryUserId: string;
    secondaryUserAccessToken: string;
}

export function useLinkAccounts() {
    const { getAccessTokenSilently } = useAuth();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (params: AccountLinkAccountsParams) => {
            const api = createAccountApi(getAccessTokenSilently);
            const response = await api.linkAccountsV1({
                userId: params.userId,
                accountLinkAccountsRequest: {
                    primaryUserId: params.primaryUserId,
                    secondaryUserId: params.secondaryUserId,
                    secondaryUserAccessToken: params.secondaryUserAccessToken,
                },
            });
            return response;
        },
    });
    return {
        linkAccounts: mutate,
        linkAccountsAsync: mutateAsync,
        ...otherProps,
    };
}

export function useLinkSamlAccounts() {
    const { user } = useAuth();

    const { linkAccountsAsync } = useLinkAccounts();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async () => {
            // TODO get these from better places
            const calmUserId = user?.sub;

            const samlUserId = getStoredUserId();
            const samlToken = getStoredIdpToken();

            if (calmUserId && samlUserId && samlToken) {
                return linkAccountsAsync({
                    userId: calmUserId,
                    primaryUserId: calmUserId,
                    secondaryUserId: samlUserId,
                    secondaryUserAccessToken: samlToken,
                });
            } else {
                throw new Error('Missing SSO linking data');
            }
        },
    });
    return {
        linkSamlAccounts: mutate,
        linkSamlAccountsAsync: mutateAsync,
        ...otherProps,
    };
}

export default useAccount;
